import React, { useState, useEffect, useRef, useCallback } from 'react';
import { connect } from 'react-redux';
import ChatDashboard from './ChatDashboard';
import Chat from './Chat';
import { useTrainerChat } from './hooks';
import { getAllOnsiteClients } from '../../ducks/Profile/actions';
import './ChatWidget.css';

/**
 * FloatingChatWidget — trainer/admin chat bubble.
 *
 * This component owns *only* the floating-bubble chrome: open/close state,
 * draggable position, ESC handler, and a polling interval that runs while
 * the bubble is open. The conversation/message orchestration (load,
 * select, mark-read, create, socket subscription, email-notify) lives in
 * the shared `useTrainerChat` hook so the dedicated `/dashboard/messages`
 * page can reuse it.
 */
function FloatingChatWidget({ currentAdmin, clients, getAllOnsiteClients }) {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState(() => ({
    x: Math.max(20, window.innerWidth - 420),
    y: Math.max(20, window.innerHeight - 620),
  }));
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const widgetRef = useRef(null);

  const {
    conversations,
    selectedConversation,
    setSelectedConversation,
    loading,
    notifyLoading,
    activeNotifiedMessageIds,
    canSendManualEmail,
    unreadCount,
    loadConversations,
    refreshUnreadCounts,
    handleConversationSelect,
    handleNewConversation,
    handleSendNotification,
  } = useTrainerChat({ currentUser: currentAdmin, clients, getAllOnsiteClients });

  // Refresh on every open — gives the user the latest threads each time
  // they pop the bubble, matching the original lazy-load semantics.
  useEffect(() => {
    if (isOpen && currentAdmin) {
      loadConversations();
    }
  }, [isOpen, currentAdmin, loadConversations]);

  // View is derived: a selected conversation = thread view, otherwise the
  // dashboard list. No separate currentView state needed.
  const view = selectedConversation ? 'chat' : 'dashboard';

  const handleBackToDashboard = () => {
    setSelectedConversation(null);
    refreshUnreadCounts();
  };

  // Periodic refresh while the bubble is open.
  useEffect(() => {
    if (!isOpen) return;
    const interval = setInterval(() => {
      refreshUnreadCounts();
    }, 30000);
    return () => clearInterval(interval);
  }, [isOpen, refreshUnreadCounts]);

  /* ----- drag chrome ----- */
  const handleMouseDown = (e) => {
    if (view === 'chat' || e.target.tagName === 'BUTTON') return;
    e.preventDefault();
    setIsDragging(true);
    setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  const handleMouseMove = useCallback(
    (e) => {
      if (!isDragging) return;
      const newX = e.clientX - dragStart.x;
      const newY = e.clientY - dragStart.y;
      const boundedX = Math.max(0, Math.min(newX, window.innerWidth - 400));
      const boundedY = Math.max(0, Math.min(newY, window.innerHeight - 600));
      setPosition({ x: boundedX, y: boundedY });
    },
    [isDragging, dragStart]
  );

  const handleMouseUp = () => setIsDragging(false);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragStart, handleMouseMove]);

  // ESC closes the widget.
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) setIsOpen(false);
    };
    if (isOpen) document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  if (!currentAdmin) return null;

  return (
    <>
      {!isOpen && (
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <button
            className='floating-chat-button'
            onClick={() => setIsOpen(true)}
            aria-label='Open chat widget'
          >
            <svg
              width='24'
              height='24'
              fill='white'
              viewBox='0 0 24 24'
              style={{ transform: 'rotate(0deg)' }}
            >
              <path d='M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z' />
            </svg>
            {unreadCount > 0 && (
              <span
                style={{
                  position: 'absolute',
                  top: '-8px',
                  right: '-8px',
                  backgroundColor: '#ef4444',
                  color: 'white',
                  borderRadius: '50%',
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  border: '2px solid white',
                }}
              >
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>
        </div>
      )}

      {isOpen && (
        <div
          ref={widgetRef}
          className={`floating-chat-widget ${isDragging ? 'dragging' : ''} ${
            window.innerWidth < 768 ? 'mobile-chat-widget' : ''
          }`}
          style={{
            left: window.innerWidth >= 768 ? `${position.x}px` : '0',
            top: window.innerWidth >= 768 ? `${position.y}px` : '0',
            width: window.innerWidth >= 768 ? '400px' : '100vw',
            height: window.innerWidth >= 768 ? '600px' : '100vh',
          }}
        >
          <div
            className={`floating-chat-header ${
              view === 'chat' ? 'not-draggable' : 'draggable'
            }`}
            onMouseDown={handleMouseDown}
          >
            <div className='header-left'>
              {view === 'chat' && (
                <button onClick={handleBackToDashboard} className='back-button'>
                  ←
                </button>
              )}
              <span className='header-title'>
                {view === 'dashboard' ? 'Chat Dashboard' : 'Chat'}
              </span>
            </div>
            <button onClick={() => setIsOpen(false)} className='close-button'>
              ×
            </button>
          </div>

          <div className='chat-content'>
            {view === 'dashboard' ? (
              <ChatDashboard
                conversations={conversations}
                clients={clients}
                currentAdmin={currentAdmin}
                onConversationSelect={handleConversationSelect}
                onNewConversation={handleNewConversation}
                loading={loading}
              />
            ) : (
              selectedConversation && (
                <Chat
                  conversationId={selectedConversation._id}
                  currentUser={{
                    id: currentAdmin.uid,
                    name: currentAdmin?.name || 'Admin',
                  }}
                  conversation={selectedConversation}
                  canSendManualEmail={canSendManualEmail}
                  onNotifyParticipant={handleSendNotification}
                  notifyLoading={notifyLoading}
                  notifiedMessageIds={activeNotifiedMessageIds}
                />
              )
            )}
          </div>
        </div>
      )}
    </>
  );
}

const mapStateToProps = (state) => ({
  currentAdmin: state.auth.user,
  clients: state.profile.onsiteClients || [],
});

const mapDispatchToProps = (dispatch) => ({
  getAllOnsiteClients: () => dispatch(getAllOnsiteClients()),
});

export default connect(mapStateToProps, mapDispatchToProps)(FloatingChatWidget);
